/**
 * Cross-domain "signed in" flag cookie (#2590).
 *
 * The marketing site (franklyadvisors.com) cannot see the SPA's Supabase
 * session — localStorage is origin-scoped. So the SPA drops a boolean
 * cookie on the parent domain when a session exists, and the marketing
 * Navigation reads it client-side to swap "Log in" for "My dashboard".
 *
 * Contract:
 * - Name `fa_signed_in`, value `1`. A flag only — NEVER token material,
 *   user ids, emails, or any PII.
 * - Production: `Domain=.franklyadvisors.com; Path=/; Max-Age=2592000
 *   (30d); SameSite=Lax; Secure`.
 * - Non-production origins (localhost, Vercel previews): host-only cookie
 *   (no Domain attribute; Secure only on https) so the write never throws
 *   and never leaks onto a shared preview domain.
 * - Cleared (Max-Age=0) on sign-out.
 */

const COOKIE_NAME = 'fa_signed_in';
const PROD_COOKIE_DOMAIN = '.franklyadvisors.com';
const THIRTY_DAYS_SECONDS = 2592000;

function cookieAttributes(): string {
  const { hostname, protocol } = window.location;
  const isProdHost =
    hostname === 'franklyadvisors.com' || hostname.endsWith('.franklyadvisors.com');
  const domain = isProdHost ? `; Domain=${PROD_COOKIE_DOMAIN}` : '';
  const secure = protocol === 'https:' ? '; Secure' : '';
  return `${domain}; Path=/; SameSite=Lax${secure}`;
}

/** Set the signed-in flag cookie. Safe to call repeatedly (idempotent). */
export function setSignedInCookie(): void {
  try {
    document.cookie = `${COOKIE_NAME}=1${cookieAttributes()}; Max-Age=${THIRTY_DAYS_SECONDS}`;
  } catch {
    // Best-effort nav personalization — never let a cookie write break auth.
  }
}

/** Clear the signed-in flag cookie (sign-out / no session). */
export function clearSignedInCookie(): void {
  try {
    document.cookie = `${COOKIE_NAME}=${cookieAttributes()}; Max-Age=0`;
  } catch {
    // Best-effort nav personalization — never let a cookie write break auth.
  }
}
